import get from "lodash.get";

function flipValue(value, order) {
  return order === "desc" ? value : -value;
}

function compare(a, b, order, orderBy, extraOrdering) {
  // Use lodash's `get` so we can get an object's value using a path
  const aValue = get(a, orderBy);
  const bValue = get(b, orderBy);

  // Check if the values are equivalent.
  // Note: Using == instead of === here to be more concise instead of checking
  // for aValue === null || aValue === undefined
  if ((aValue == null && bValue == null) || aValue === bValue) {
    // Apply extra ordering if values are equivalent
    // Example extraOrdering value:
    // [['desc', 'someField'], ['asc', 'otherField']]
    if (extraOrdering && extraOrdering.length > 0) {
      const [[newOrder, newOrderBy], ...rest] = extraOrdering;
      return compare(a, b, newOrder, newOrderBy, rest);
    }

    return 0;
  }

  // Handle cases where sorting is done incorrectly if there are null/undefined
  // values
  if (aValue == null) {
    return flipValue(1, order);
  } else if (bValue == null) {
    return flipValue(-1, order);
  }

  const comparisonValue = String(bValue).localeCompare(
    String(aValue),
    undefined,
    {
      sensitivity: "base",
      ignorePunctuation: true,
      numeric: !isNaN(aValue) && !isNaN(bValue),
    }
  );
  return flipValue(comparisonValue, order);
}

function getSorting(order, orderBy, extraOrdering) {
  return (a, b) => compare(a, b, order, orderBy, extraOrdering);
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

/*
  - Compares two values after coverting to Float
  - Currently used on CyPortfolioDetailsSortable component
*/
function compareFloatOutput(a, b, order, orderBy) {
  const aValue = parseFloat(get(a, orderBy));
  const bValue = parseFloat(get(b, orderBy));
  if (aValue < bValue) {
    return flipValue(-1, order);
  }
  if (aValue > bValue) {
    return flipValue(1, order);
  }
  return flipValue(0, order);
}

/*
  - Check the orderBy which are the numbers in string
  - Return compareFloatOutput(f) for the number data or
  - Return compare(f) for the string data
*/
function sortPortfolioOutput(order, orderBy) {
  if (
    orderBy === "managerWeight" ||
    orderBy === "managerDetail.UpCapture" ||
    orderBy === "managerDetail.DownCapture" ||
    orderBy === "managerDetail.MaxDrawdown" ||
    orderBy === "managerDetail.Worst4QtrReturn" ||
    orderBy === "managerDetail.WorstCalendarYearReturn" ||
    orderBy === "managerDetail.AnnualizedReturn" ||
    orderBy === "managerFinalWealth"
  ) {
    return (a, b) => compareFloatOutput(a, b, order, orderBy);
  }
  return (a, b) => compare(a, b, order, orderBy);
}

/*
  - Check the data which has negative trial remaining day and
    more than 0 total onboarded
  - Edit this trial remaining data to list at last
*/
function compareTrialRemainingOutput(a, b, order, orderBy) {
  let aValue = parseFloat(get(a, orderBy));
  let bValue = parseFloat(get(b, orderBy));
  const aTotalOnboarded = parseFloat(get(a, "totalOnboarded"));
  const bTotalOnboarded = parseFloat(get(b, "totalOnboarded"));
  if (aValue < 0 && aTotalOnboarded > 0) {
    aValue = order === "desc" ? Date.now() : -Date.now();
  }

  if (bValue < 0 && bTotalOnboarded > 0) {
    bValue = order === "desc" ? Date.now() + 10 : -(Date.now() + 10);
  }

  if (aValue < bValue) {
    return flipValue(-1, order);
  }
  if (aValue > bValue) {
    return flipValue(1, order);
  }
  return flipValue(0, order);
}

function compareOnboardedOutput(a, b, order, orderBy, extraOrdering) {
  const aValue = a.Portfolios.filter((p) => p.onboarded).length;
  const bValue = b.Portfolios.filter((p) => p.onboarded).length;

  if (aValue < bValue) {
    return flipValue(-1, order);
  }
  if (aValue > bValue) {
    return flipValue(1, order);
  }

  if (extraOrdering && extraOrdering.length > 0) {
    const [[newOrder, newOrderBy], ...rest] = extraOrdering;
    return compare(a, b, newOrder, newOrderBy, rest);
  }

  return flipValue(0, order);
}

/*
  - Return compareTrialRemainingOutput(f) for the trial remaining sort
  - Return compare(f) for any other column sorting
*/
function sortAdvisorList(order, orderBy) {
  if (orderBy === "trialRemaining") {
    return (a, b) => compareTrialRemainingOutput(a, b, order, orderBy);
  }
  return (a, b) => compare(a, b, order, orderBy);
}

function sortAdvisorDetail(order, orderBy, extraOrdering) {
  if (orderBy === "onboarded") {
    return (a, b) =>
      compareOnboardedOutput(a, b, order, orderBy, extraOrdering);
  }
  return (a, b) => compare(a, b, order, orderBy, extraOrdering);
}

export {
  getSorting,
  sortAdvisorDetail,
  sortAdvisorList,
  sortPortfolioOutput,
  stableSort,
};



// WEBPACK FOOTER //
// ./src/utilities/sorting.js